import React, { useState, useRef, useEffect } from 'react';
import { getVolume, speakText, setReplay } from './lzAudio';

// Block-based coding for ages 3-5, built around short missions: each one
// has a spoken goal, checks the result when the program finishes, and
// awards 1-3 stars by how few blocks were used. "Free play" keeps the
// original sandbox (star / rain / apple toggles).
//
// Execution rules: MOVE/DO blocks run in order; a REPEAT block runs the
// next block N times; an IF block runs the next block only when its
// condition is true right now.

const STEPS = 8; // positions 0..7; the apple tree stands at the last one
const TREE_STEP = STEPS - 1;
const MAX_BLOCKS = 8;
const PAUSE = 160;
const ACTION_MS = 520;

const CHARACTERS = [
    { id: 'cat', emoji: '🐱', label: 'Cat' },
    { id: 'robot', emoji: '🤖', label: 'Robot' },
    { id: 'bunny', emoji: '🐰', label: 'Bunny' },
];

const GROUPS = {
    move: { label: 'Move', color: '#2563EB', light: '#DBEAFE', edge: '#1D4ED8' },
    do: { label: 'Do', color: '#EA580C', light: '#FFEDD5', edge: '#C2410C' },
    repeat: { label: 'Repeat', color: '#7C3AED', light: '#EDE9FE', edge: '#6D28D9' },
    if: { label: 'If', color: '#16A34A', light: '#DCFCE7', edge: '#15803D' },
};

const BLOCKS = [
    { type: 'right', group: 'move', icon: '➡️', label: 'Go right' },
    { type: 'left', group: 'move', icon: '⬅️', label: 'Go left' },
    { type: 'jump', group: 'move', icon: '⤴️', label: 'Jump' },
    { type: 'dance', group: 'do', icon: '💃', label: 'Dance' },
    { type: 'hi', group: 'do', icon: '👋', label: 'Say hi' },
    { type: 'eat', group: 'do', icon: '😋', label: 'Eat' },
    { type: 'sleep', group: 'do', icon: '😴', label: 'Sleep' },
    { type: 'spin', group: 'do', icon: '🌀', label: 'Spin' },
    { type: 'repeat2', group: 'repeat', icon: '🔁', label: '×2', times: 2 },
    { type: 'repeat3', group: 'repeat', icon: '🔁', label: '×3', times: 3 },
    { type: 'ifStar', group: 'if', icon: '⭐', label: 'If ⭐', cond: 'star' },
    { type: 'ifRain', group: 'if', icon: '🌧️', label: 'If 🌧️', cond: 'rain' },
    { type: 'ifApple', group: 'if', icon: '🍎', label: 'If 🍎', cond: 'apple' },
];
const BLOCK = Object.fromEntries(BLOCKS.map((b) => [b.type, b]));

// best = fewest blocks that solve it (3 stars at or under, 2 within +2).
const MISSIONS = [
    {
        id: 1, icon: '⭐', title: 'Walk to the star', goal: 'Help your friend walk to the star!',
        scene: { star: true, rain: false, apple: false }, starStep: 3, best: 3,
        hint: 'Tap ➡️ Go right three times, then press Play!',
        check: (s) => s.gotStar,
    },
    {
        id: 2, icon: '🌟', title: 'The far-away star', goal: 'The star is far away. Can you reach it?',
        scene: { star: true, rain: false, apple: false }, starStep: 6, best: 4,
        hint: 'Try 🔁 ×3 then ➡️ Go right — do it two times!',
        check: (s) => s.gotStar,
    },
    {
        id: 3, icon: '🍎', title: 'Hungry! Eat the apple', goal: 'Walk to the apple tree and eat the apple!',
        scene: { star: false, rain: false, apple: true }, best: 6,
        hint: 'Walk all the way to the tree (use 🔁!), then 😋 Eat.',
        check: (s) => s.ate,
    },
    {
        id: 4, icon: '🦘', title: 'Bouncy! Jump 3 times', goal: 'Make your friend jump three times!',
        scene: { star: false, rain: false, apple: false }, best: 2,
        hint: 'Put 🔁 ×3 before ⤴️ Jump.',
        check: (s) => s.jumps >= 3,
    },
    {
        id: 5, icon: '👋', title: 'Say hi, then dance', goal: 'First say hi, then do a happy dance!',
        scene: { star: false, rain: false, apple: false }, best: 2,
        hint: 'Order matters: 👋 Say hi first, then 💃 Dance.',
        check: (s) => { const h = s.log.indexOf('hi'); return h >= 0 && s.log.indexOf('dance', h) > h; },
    },
    {
        id: 6, icon: '🌧️', title: 'Rainy day nap', goal: 'It is raining! If it rains, take a nap.',
        scene: { star: false, rain: true, apple: false }, best: 2,
        hint: 'Use 🌧️ If rain, then 😴 Sleep right after it.',
        check: (s) => s.ifSleep,
    },
    {
        id: 7, icon: '🏆', title: 'Star, then snack!', goal: 'Grab the star, then eat the apple!',
        scene: { star: true, rain: false, apple: true }, starStep: 2, best: 6,
        hint: 'Walk right all the way to the tree — you will pass the star — then 😋 Eat.',
        check: (s) => s.gotStar && s.ate,
    },
    {
        id: 8, icon: '🎨', title: 'Free play', goal: 'Build anything you like!', free: true,
        scene: { star: true, rain: false, apple: true }, starStep: 4,
    },
];
export const CODING_MISSION_COUNT = MISSIONS.filter((m) => !m.free).length;

const stepLeft = (i) => `${4 + i * 11}%`;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const starsFor = (blocks, best) => (blocks <= best ? 3 : blocks <= best + 2 ? 2 : 1);
const CONFETTI = ['#FF3B30', '#FFCC00', '#28A745', '#1A6EFF', '#8B2BE2', '#FF69B4'];

const PuzzleBlockCoding = ({ childId, childAge, onComplete, onMissionComplete }) => {
    const storageKey = childId ? `syntch_coding_missions_${childId}` : 'syntch_coding_missions';

    const [missionIdx, setMissionIdx] = useState(0);
    const [progress, setProgress] = useState({}); // { missionId: stars }
    const [character, setCharacter] = useState('cat');
    const [freeScene, setFreeScene] = useState(MISSIONS[MISSIONS.length - 1].scene);
    const [program, setProgram] = useState([]);
    const [running, setRunning] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [pos, setPos] = useState(0);
    const [action, setAction] = useState(null);
    const [bubble, setBubble] = useState(null);
    const [starLeft, setStarLeft] = useState(true);
    const [appleLeft, setAppleLeft] = useState(true);
    const [score, setScore] = useState(0);
    const [popups, setPopups] = useState([]);
    const [result, setResult] = useState(null); // { ok, stars? }

    const runRef = useRef(false);
    const reportedRef = useRef(false);
    const mounted = useRef(true);
    const audioCtx = useRef(null);
    const live = useRef({});

    const mission = MISSIONS[missionIdx];
    const scene = mission.free ? freeScene : mission.scene;
    const starStep = mission.starStep ?? 4;

    useEffect(() => {
        setReplay(mission.goal);
        return () => setReplay(null);
    }, [mission]);

    useEffect(() => () => {
        mounted.current = false;
        runRef.current = false;
        try { window.speechSynthesis?.cancel(); } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            const parsed = raw ? JSON.parse(raw) : {};
            setProgress(parsed && typeof parsed === 'object' ? parsed : {});
        } catch (e) { setProgress({}); }
    }, [storageKey]);

    // ─── Sound ──────────────────────────────────────────────────────────────
    const tone = (freq, delayMs = 0, gain = 0.12, dur = 0.16, type = 'sine') => {
        const vol = getVolume();
        if (!vol) return;
        try {
            if (!audioCtx.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return;
                audioCtx.current = new Ctx();
            }
            const ctx = audioCtx.current;
            if (ctx.state === 'suspended') ctx.resume();
            const t = ctx.currentTime + delayMs / 1000;
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = type;
            osc.frequency.value = freq;
            g.gain.setValueAtTime(gain * vol, t);
            g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
            osc.connect(g).connect(ctx.destination);
            osc.start(t);
            osc.stop(t + dur + 0.02);
        } catch (e) { /* ignore */ }
    };
    const say = (text) => speakText(text);
    const playCollect = () => [659, 784, 1047].forEach((f, i) => tone(f, i * 100, 0.16));
    const playWin = () => [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 120, 0.16, 0.22));

    // ─── Scene / mission control ────────────────────────────────────────────
    const resetScene = () => {
        setPos(0);
        setAction(null);
        setBubble(null);
        setStarLeft(true);
        setAppleLeft(true);
        setScore(0);
        setActiveIndex(null);
        setResult(null);
    };

    const isUnlocked = (i) => MISSIONS[i].free || i === 0 || progress[MISSIONS[i - 1].id] > 0;

    const openMission = (i) => {
        if (running || !isUnlocked(i)) return;
        setMissionIdx(i);
        setProgram([]);
        resetScene();
        say(MISSIONS[i].goal);
    };

    const addBlock = (type) => {
        if (running || program.length >= MAX_BLOCKS) return;
        tone(440, 0, 0.1, 0.1);
        setResult(null);
        setProgram((p) => [...p, { key: `${type}-${Date.now()}-${Math.random()}`, type }]);
    };
    const removeBlock = (index) => {
        if (running) return;
        tone(300, 0, 0.1, 0.1);
        setResult(null);
        setProgram((p) => p.filter((_, i) => i !== index));
    };

    // ─── Execution ──────────────────────────────────────────────────────────
    const popup = (text) => {
        const id = `${Date.now()}-${Math.random()}`;
        setPopups((p) => [...p, { id, text, step: live.current.pos }]);
        setTimeout(() => mounted.current && setPopups((p) => p.filter((x) => x.id !== id)), 900);
    };

    const moveTo = (next) => {
        const clamped = Math.max(0, Math.min(STEPS - 1, next));
        live.current.pos = clamped;
        setPos(clamped);
        if (clamped === starStep && scene.star && live.current.starLeft) {
            live.current.starLeft = false;
            live.current.gotStar = true;
            live.current.score += 10;
            setStarLeft(false);
            setScore(live.current.score);
            playCollect();
            popup('⭐ +10');
        }
    };

    const doAction = async (type, viaIf = null) => {
        setBubble(null);
        live.current.log.push(type);
        switch (type) {
            case 'right': tone(520, 0, 0.1, 0.1); moveTo(live.current.pos + 1); break;
            case 'left': tone(480, 0, 0.1, 0.1); moveTo(live.current.pos - 1); break;
            case 'jump': tone(600); live.current.jumps += 1; setAction('jump'); break;
            case 'dance': [523, 659, 523].forEach((f, i) => tone(f, i * 150)); setAction('dance'); break;
            case 'hi': tone(700); setBubble('Hi! 👋'); break;
            case 'spin': tone(560); setAction('spin'); break;
            case 'sleep':
                tone(260, 0, 0.1, 0.4);
                setAction('sleep');
                setBubble('Zzz… 💤');
                if (viaIf === 'rain') live.current.ifSleep = true;
                break;
            case 'eat':
                if (scene.apple && live.current.appleLeft && live.current.pos === TREE_STEP) {
                    live.current.appleLeft = false;
                    live.current.ate = true;
                    live.current.score += 5;
                    setAppleLeft(false);
                    setScore(live.current.score);
                    playCollect();
                    setBubble('Yum! 😋');
                    popup('🍎 +5');
                } else {
                    tone(330);
                    setBubble(scene.apple && live.current.appleLeft ? 'Too far from the tree!' : 'No apple here…');
                }
                break;
            default: break;
        }
        await wait(ACTION_MS);
        if (mounted.current) setAction(null);
    };

    const condTrue = (cond) => {
        if (cond === 'star') return !!scene.star && live.current.starLeft;
        if (cond === 'apple') return !!scene.apple && live.current.appleLeft;
        return !!scene.rain;
    };

    const run = async () => {
        if (running || program.length === 0) return;
        resetScene();
        live.current = { pos: 0, starLeft: true, appleLeft: true, score: 0, jumps: 0, log: [], gotStar: false, ate: false, ifSleep: false };
        runRef.current = true;
        setRunning(true);
        await wait(250);

        let i = 0;
        while (i < program.length && runRef.current) {
            const block = BLOCK[program[i].type];
            setActiveIndex(i);

            if (block.group === 'repeat' || block.group === 'if') {
                await wait(ACTION_MS / 2);
                const nextIdx = i + 1;
                const next = program[nextIdx] && BLOCK[program[nextIdx].type];
                let times = block.times || 0;
                if (block.group === 'if') {
                    const ok = condTrue(block.cond);
                    times = ok ? 1 : 0;
                    setBubble(ok ? '✅ Yes!' : '❌ Not now');
                    tone(ok ? 660 : 240, 0, 0.12, 0.2, ok ? 'sine' : 'triangle');
                    if (ok && !reportedRef.current) {
                        reportedRef.current = true;
                        onComplete?.({
                            childId, character, condition: block.cond, missionId: mission.id,
                            program: program.map((p) => p.type), blocksUsed: program.length,
                            score: live.current.score, completedAt: new Date().toISOString(),
                        });
                    }
                    await wait(ACTION_MS / 2);
                }
                if (next && (next.group === 'move' || next.group === 'do')) {
                    setActiveIndex(nextIdx);
                    for (let n = 0; n < times && runRef.current; n++) {
                        await doAction(next.type, block.group === 'if' ? block.cond : null);
                        await wait(PAUSE);
                    }
                    i = nextIdx + 1;
                } else {
                    i += 1;
                }
                await wait(PAUSE);
                continue;
            }

            await doAction(block.type);
            await wait(PAUSE);
            i += 1;
        }

        if (!mounted.current) return;
        const stopped = !runRef.current;
        runRef.current = false;
        setRunning(false);
        setActiveIndex(null);
        if (stopped) return;

        if (mission.free) {
            setResult({ ok: true, free: true });
            playWin();
            say('Great coding!');
            return;
        }
        if (mission.check(live.current)) {
            const stars = starsFor(program.length, mission.best);
            const next = { ...progress, [mission.id]: Math.max(progress[mission.id] || 0, stars) };
            setProgress(next);
            try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
            setResult({ ok: true, stars });
            playWin();
            say('Mission complete! Great job!');
            onMissionComplete?.(mission.id, stars, CODING_MISSION_COUNT);
        } else {
            setResult({ ok: false });
            tone(220, 0, 0.1, 0.3, 'triangle');
            say('Almost! Try again.');
        }
    };

    // ─── Render ─────────────────────────────────────────────────────────────
    const charEmoji = CHARACTERS.find((c) => c.id === character).emoji;
    const charAnim = { jump: 'bc-jump .5s ease', dance: 'bc-dance .5s ease', spin: 'bc-spin .5s linear', sleep: 'bc-sleep .5s ease' }[action] || 'none';
    const nextMissionIdx = missionIdx + 1 < MISSIONS.length ? missionIdx + 1 : null;

    return (
        <div className="bc-root">
            <style>{`
                .bc-root {
                    --bc-w: clamp(260px, calc((100vh - 506px) * 2.55), 600px);
                    position: relative; padding: 10px 12px; color: #3e2723; user-select: none;
                    font-family: 'Baloo 2', 'Fredoka One', 'Comic Sans MS', cursive;
                }
                .bc-missions { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
                .bc-mlist { display: flex; gap: 6px; overflow-x: auto; padding: 8px 2px 6px; flex: 1; }
                .bc-m {
                    position: relative; flex-shrink: 0; width: 42px; height: 42px; border-radius: 13px; font-size: 20px; cursor: pointer;
                    border: 3px solid #FFE0B2; background: #FFF8E1; box-shadow: 0 3px 0 #FFE0B2; font-family: inherit; padding: 0;
                }
                .bc-m.active { border-color: #FF9800; background: #FFE0B2; box-shadow: 0 3px 0 #FF9800; transform: translateY(-2px); }
                .bc-m.locked { filter: grayscale(1); opacity: .45; cursor: not-allowed; }
                .bc-m .bc-mnum { position: absolute; top: -8px; left: -6px; width: 18px; height: 18px; border-radius: 50%; background: #3e2723; color: #fff; font-size: 10px; font-weight: 900; display: flex; align-items: center; justify-content: center; }
                .bc-m .bc-mstars { position: absolute; bottom: -9px; left: 50%; transform: translateX(-50%); font-size: 9px; white-space: nowrap; letter-spacing: -1px; }
                .bc-icon-btn { flex-shrink: 0; width: 38px; height: 38px; border-radius: 50%; border: 3px solid #B3E5FC; background: #E1F5FE; font-size: 19px; cursor: pointer; }
                .bc-chars { display: flex; gap: 4px; flex-shrink: 0; }
                .bc-char { width: 38px; height: 38px; border-radius: 50%; border: 3px solid #E2E8F0; background: #fff; font-size: 20px; cursor: pointer; padding: 0; }
                .bc-char.active { border-color: #2563EB; background: #DBEAFE; }

                .bc-goal {
                    display: flex; align-items: center; gap: 10px; padding: 4px 8px 4px 12px; margin-bottom: 8px; border-radius: 16px;
                    background: linear-gradient(90deg, #FFF3C4, #FFE0EC); border: 3px solid #FFD54F;
                }
                .bc-goal-icon { font-size: 24px; }
                .bc-goal-title { font-weight: 900; font-size: 15px; }
                .bc-goal-text { font-weight: 700; font-size: 13px; color: #6D4C41; }
                .bc-speak { margin-left: auto; flex-shrink: 0; border: none; background: #fff; border-radius: 12px; padding: 6px 10px; font-weight: 900; cursor: pointer; font-family: inherit; color: #6D4C41; box-shadow: 0 2px 0 #FFD54F; }

                .bc-main { display: grid; grid-template-columns: var(--bc-w) minmax(340px, 380px); gap: 14px; justify-content: center; align-items: start; }
                .bc-left { display: flex; flex-direction: column; gap: 8px; width: var(--bc-w); }

                .bc-scene { position: relative; width: 100%; aspect-ratio: 560 / 220; border-radius: 18px; overflow: hidden; border: 4px solid #fff; box-shadow: 0 6px 18px rgba(0,0,0,.15); }
                .bc-scene svg { position: absolute; inset: 0; width: 100%; height: 100%; }
                .bc-drop { position: absolute; top: -12%; width: 2px; height: 10%; background: #1E88E5; border-radius: 2px; opacity: .7; animation: bc-rain linear infinite; }
                .bc-star { position: absolute; top: 34%; font-size: clamp(20px, 5.5%, 34px); animation: bc-twinkle 1.2s ease infinite; transform: translateX(10%); }
                .bc-char-wrap { position: absolute; bottom: 20%; width: 9%; text-align: center; transition: left .42s ease; }
                .bc-char-emoji { font-size: clamp(30px, calc(var(--bc-w) * 0.075), 48px); line-height: 1; display: inline-block; }
                .bc-bubble { position: absolute; bottom: 105%; left: 50%; transform: translateX(-50%); white-space: nowrap; background: #fff; border-radius: 12px; padding: 3px 10px; font-size: 13px; font-weight: 900; box-shadow: 0 2px 8px rgba(0,0,0,.2); animation: bc-pop .25s ease; }
                .bc-popup { position: absolute; top: 30%; font-weight: 900; font-size: 20px; color: #F57F17; text-shadow: 0 2px 0 #fff; animation: bc-float .9s ease-out forwards; pointer-events: none; }
                .bc-score { position: absolute; top: 8px; right: 10px; background: rgba(255,255,255,.9); border-radius: 12px; padding: 2px 12px; font-weight: 900; font-size: 15px; }
                .bc-flag { position: absolute; top: 8px; left: 10px; background: rgba(255,255,255,.9); border-radius: 12px; padding: 2px 10px; font-weight: 900; font-size: 12px; color: #6D4C41; }

                .bc-toggles { display: flex; gap: 6px; justify-content: center; }
                .bc-toggle { min-height: 34px; padding: 2px 12px; border-radius: 17px; font-weight: 900; font-size: 13px; cursor: pointer; font-family: inherit; border: 2px solid #CBD5E1; background: #F8FAFC; color: #64748B; }
                .bc-toggle.on { border-color: #16A34A; background: #DCFCE7; color: #166534; }

                .bc-program { background: #fff; border: 3px dashed #CBD5E1; border-radius: 18px; padding: 4px 10px; min-height: 70px; display: flex; flex-direction: column; justify-content: center; }
                .bc-program-head { font-size: 12px; font-weight: 900; color: #64748B; display: flex; justify-content: space-between; }
                .bc-train { display: flex; align-items: center; gap: 3px; overflow-x: auto; padding: 8px 2px 2px; min-height: 52px; }
                .bc-empty { color: #94A3B8; font-weight: 800; font-size: 14px; }
                .bc-arrow { color: #94A3B8; font-weight: 900; }

                .bc-block {
                    position: relative; flex-shrink: 0; width: 56px; height: 46px; border-radius: 13px; border: 3px solid; cursor: pointer; padding: 0;
                    display: flex; flex-direction: column; align-items: center; justify-content: center; line-height: 1.05;
                    font-weight: 900; font-size: 10px; font-family: inherit; white-space: nowrap; transition: transform .12s, box-shadow .12s;
                }
                .bc-block span { font-size: 18px; }
                .bc-block:not(:disabled):hover { transform: translateY(-2px); }
                .bc-block:disabled { cursor: not-allowed; opacity: .5; }
                .bc-block.active { background: #FDE047 !important; border-color: #CA8A04 !important; transform: scale(1.1); box-shadow: 0 0 0 4px #FEF08A; }
                .bc-block .bc-n { position: absolute; top: -8px; left: -6px; width: 17px; height: 17px; border-radius: 50%; background: #3e2723; color: #fff; font-size: 10px; display: flex; align-items: center; justify-content: center; }

                .bc-controls { display: flex; gap: 8px; justify-content: center; }
                .bc-btn { min-height: 42px; padding: 0 18px; border-radius: 21px; border: none; font-weight: 900; font-size: 16px; cursor: pointer; font-family: inherit; color: #fff; }
                .bc-btn:disabled { opacity: .45; cursor: not-allowed; }
                .bc-btn.play { background: #16A34A; box-shadow: 0 4px 0 #15803D; font-size: 20px; padding: 0 28px; }
                .bc-btn.stop { background: #DC2626; box-shadow: 0 4px 0 #B91C1C; font-size: 20px; padding: 0 28px; }
                .bc-btn.light { background: #fff; color: #3e2723; border: 2px solid #CBD5E1; box-shadow: 0 3px 0 #CBD5E1; }
                .bc-btn:active { transform: translateY(2px); }

                .bc-palette { background: #fff; border-radius: 18px; padding: 8px 12px; box-shadow: 0 3px 12px rgba(0,0,0,.07); border: 3px solid #E0E7FF; }
                .bc-palette-title { font-weight: 900; font-size: 14px; margin-bottom: 8px; }
                .bc-group { display: flex; align-items: center; gap: 6px; margin-bottom: 7px; }
                .bc-group-label { width: 44px; flex-shrink: 0; font-size: 10px; font-weight: 900; text-transform: uppercase; letter-spacing: .5px; }
                .bc-group-blocks { display: flex; flex-wrap: wrap; gap: 5px; }
                .bc-tip { font-size: 11px; color: #64748B; font-weight: 700; margin-top: 2px; }

                .bc-result {
                    position: absolute; inset: 0; z-index: 20; display: flex; align-items: center; justify-content: center;
                    background: rgba(255,255,255,.88); border-radius: 20px; overflow: hidden;
                }
                .bc-result-box { text-align: center; background: #fff; border-radius: 24px; padding: 18px 26px; box-shadow: 0 10px 30px rgba(0,0,0,.18); border: 4px solid; animation: bc-pop .35s ease; max-width: 90%; }
                .bc-result-stars { font-size: 36px; margin: 4px 0 10px; }
                .bc-confetti { position: absolute; top: -20px; width: 10px; height: 16px; border-radius: 3px; animation: bc-fall linear forwards; }

                /* Short laptop screens: trim the block palette so nothing scrolls. */
                @media (max-height: 680px) {
                    .bc-block { height: 40px; }
                    .bc-block span { font-size: 16px; }
                    .bc-group { margin-bottom: 4px; }
                    .bc-tip, .bc-goal-text { display: none; }
                    .bc-program { min-height: 62px; }
                    .bc-train { min-height: 46px; }
                    .bc-btn { min-height: 34px; }
                    .bc-palette { padding: 6px 10px; }
                    .bc-palette-title { margin-bottom: 4px; font-size: 13px; }
                    .bc-goal { margin-bottom: 6px; }
                    .bc-missions { margin-bottom: 4px; }
                    .bc-btn.play, .bc-btn.stop { font-size: 17px; }
                    .bc-m, .bc-char, .bc-icon-btn { width: 36px; height: 36px; }
                }
                @media (max-width: 1000px) {
                    .bc-root { --bc-w: min(600px, calc(100vw - 90px)); }
                    .bc-main { grid-template-columns: 1fr; justify-items: center; }
                    .bc-palette { width: var(--bc-w); box-sizing: border-box; }
                }

                @keyframes bc-jump { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-70%) } }
                @keyframes bc-dance { 0%,100% { transform: rotate(0) } 25% { transform: rotate(-18deg) } 75% { transform: rotate(18deg) } }
                @keyframes bc-spin { from { transform: rotate(0) } to { transform: rotate(360deg) } }
                @keyframes bc-sleep { 0%,100% { transform: scale(1) } 50% { transform: scale(.88) translateY(6%) } }
                @keyframes bc-rain { from { transform: translateY(0) } to { transform: translateY(1200%) } }
                @keyframes bc-twinkle { 0%,100% { transform: translateX(10%) scale(1) } 50% { transform: translateX(10%) scale(1.2) } }
                @keyframes bc-pop { 0% { transform: scale(0) } 70% { transform: scale(1.1) } 100% { transform: scale(1) } }
                @keyframes bc-float { 0% { opacity: 1; transform: translateY(0) } 100% { opacity: 0; transform: translateY(-40px) } }
                @keyframes bc-fall { to { transform: translateY(600px) rotate(720deg) } }
            `}</style>

            {/* Missions + character + sound */}
            <div className="bc-missions">
                <div className="bc-mlist">
                    {MISSIONS.map((m, i) => {
                        const unlocked = isUnlocked(i);
                        const stars = progress[m.id] || 0;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                title={unlocked ? m.title : 'Finish the mission before this one'}
                                className={`bc-m ${i === missionIdx ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
                                onClick={() => openMission(i)}
                            >
                                {!m.free && <span className="bc-mnum">{m.id}</span>}
                                {unlocked ? m.icon : '🔒'}
                                {stars > 0 && <span className="bc-mstars">{'⭐'.repeat(stars)}</span>}
                            </button>
                        );
                    })}
                </div>
                <div className="bc-chars">
                    {CHARACTERS.map((c) => (
                        <button key={c.id} type="button" title={c.label} disabled={running} className={`bc-char ${character === c.id ? 'active' : ''}`} onClick={() => setCharacter(c.id)}>{c.emoji}</button>
                    ))}
                </div>
            </div>

            {/* Goal */}
            <div className="bc-goal">
                <span className="bc-goal-icon">{mission.icon}</span>
                <div>
                    <div className="bc-goal-title">{mission.free ? 'Free play' : `Mission ${mission.id}: ${mission.title}`}</div>
                    <div className="bc-goal-text">{mission.goal}</div>
                </div>
                <button type="button" className="bc-speak" onClick={() => say(mission.goal)}>🔈 Listen</button>
            </div>

            <div className="bc-main">
                <div className="bc-left">
                    {/* Scene */}
                    <div className="bc-scene">
                        <svg viewBox="0 0 560 220" preserveAspectRatio="none">
                            <rect width="560" height="220" fill={scene.rain ? '#B0BEC5' : '#BFE9FF'} />
                            {!scene.rain && <circle cx="46" cy="40" r="24" fill="#FFD54F" />}
                            <g fill={scene.rain ? '#90A4AE' : '#fff'}>
                                <circle cx="170" cy="36" r="15" /><circle cx="190" cy="28" r="19" /><circle cx="210" cy="37" r="14" />
                                <circle cx="340" cy="46" r="13" /><circle cx="357" cy="39" r="17" /><circle cx="374" cy="47" r="12" />
                            </g>
                            <rect y="168" width="560" height="52" fill="#7CC576" />
                            <rect y="176" width="560" height="22" fill="#D7B98E" />
                            {Array.from({ length: STEPS }).map((_, i) => (
                                <circle key={i} cx={(4 + i * 11 + 4.5) * 5.6} cy="187" r="5" fill={i === TREE_STEP ? '#8D6E63' : '#fff'} opacity=".75" />
                            ))}
                            <rect x="512" y="100" width="16" height="76" rx="5" fill="#8D6E63" />
                            <circle cx="520" cy="88" r="34" fill="#43A047" />
                            <circle cx="498" cy="104" r="20" fill="#4CAF50" />
                            <circle cx="542" cy="104" r="18" fill="#388E3C" />
                            {scene.apple && appleLeft && <circle cx="532" cy="104" r="9" fill="#E53935" />}
                        </svg>

                        {scene.rain && Array.from({ length: 18 }).map((_, i) => (
                            <span key={i} className="bc-drop" style={{ left: `${3 + i * 5.5}%`, animationDuration: `${0.7 + (i % 3) * 0.2}s`, animationDelay: `${i * 0.06}s` }} />
                        ))}
                        {scene.star && starLeft && <span className="bc-star" style={{ left: stepLeft(starStep) }}>⭐</span>}
                        {popups.map((p) => <span key={p.id} className="bc-popup" style={{ left: stepLeft(p.step) }}>{p.text}</span>)}

                        <div className="bc-char-wrap" style={{ left: stepLeft(pos) }}>
                            {bubble && <div className="bc-bubble">{bubble}</div>}
                            <span className="bc-char-emoji" style={{ animation: charAnim }}>{charEmoji}</span>
                        </div>
                        <div className="bc-flag">🏁 Start</div>
                        <div className="bc-score">🏆 {score}</div>
                    </div>

                    {mission.free && (
                        <div className="bc-toggles">
                            {[['star', '⭐ Star'], ['rain', '🌧️ Rain'], ['apple', '🍎 Apple']].map(([k, label]) => (
                                <button key={k} type="button" disabled={running} className={`bc-toggle ${freeScene[k] ? 'on' : ''}`} onClick={() => { setFreeScene((s) => ({ ...s, [k]: !s[k] })); resetScene(); }}>
                                    {label}: {freeScene[k] ? 'ON' : 'OFF'}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Program train */}
                    <div className="bc-program">
                        <div className="bc-program-head">
                            <span>🧩 My program</span>
                            <span>{program.length} / {MAX_BLOCKS} blocks · tap a block to remove it</span>
                        </div>
                        <div className="bc-train">
                            {program.length === 0 && <span className="bc-empty">👉 Tap the blocks on the right to build your program!</span>}
                            {program.map((p, i) => {
                                const b = BLOCK[p.type];
                                const g = GROUPS[b.group];
                                return (
                                    <React.Fragment key={p.key}>
                                        {i > 0 && <span className="bc-arrow">›</span>}
                                        <button
                                            type="button"
                                            className={`bc-block ${activeIndex === i ? 'active' : ''}`}
                                            onClick={() => removeBlock(i)}
                                            style={{ background: g.light, borderColor: g.color, color: g.edge, animation: 'bc-pop .2s ease' }}
                                        >
                                            <span className="bc-n">{i + 1}</span>
                                            <span>{b.icon}</span>{b.label}
                                        </button>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bc-controls">
                        {!running ? (
                            <button type="button" className="bc-btn play" onClick={run} disabled={program.length === 0}>▶ Play</button>
                        ) : (
                            <button type="button" className="bc-btn stop" onClick={() => { runRef.current = false; }}>■ Stop</button>
                        )}
                        <button type="button" className="bc-btn light" disabled={running || program.length === 0} onClick={() => { setProgram([]); resetScene(); }}>🗑 Clear</button>
                        <button type="button" className="bc-btn light" disabled={running} onClick={resetScene}>↺ Reset</button>
                    </div>
                </div>

                {/* Palette */}
                <div className="bc-palette">
                    <div className="bc-palette-title">🧱 Blocks — tap to add</div>
                    {Object.entries(GROUPS).map(([gid, g]) => (
                        <div key={gid} className="bc-group">
                            <div className="bc-group-label" style={{ color: g.color }}>{g.label}</div>
                            <div className="bc-group-blocks">
                                {BLOCKS.filter((b) => b.group === gid).map((b) => (
                                    <button
                                        key={b.type}
                                        type="button"
                                        className="bc-block"
                                        disabled={running || program.length >= MAX_BLOCKS}
                                        onClick={() => addBlock(b.type)}
                                        style={{ background: g.light, borderColor: g.color, color: g.edge, boxShadow: `0 3px 0 ${g.color}` }}
                                    >
                                        <span>{b.icon}</span>{b.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div className="bc-tip">💡 🔁 and IF work on the block right after them.</div>
                </div>
            </div>

            {result && (
                <div className="bc-result" onClick={() => !result.ok && setResult(null)}>
                    {result.ok && Array.from({ length: 30 }).map((_, i) => (
                        <span key={i} className="bc-confetti" style={{ left: `${(i * 37) % 100}%`, background: CONFETTI[i % CONFETTI.length], animationDuration: `${1.5 + (i % 5) * 0.3}s`, animationDelay: `${(i % 6) * 0.08}s` }} />
                    ))}
                    <div className="bc-result-box" style={{ borderColor: result.ok ? '#22C55E' : '#F59E0B' }} onClick={(e) => e.stopPropagation()}>
                        {result.ok ? (
                            <>
                                <div style={{ fontSize: 56 }}>{result.free ? '🎉' : '🏆'}</div>
                                <div style={{ fontSize: 26, fontWeight: 900 }}>{result.free ? 'Great coding!' : 'Mission complete!'}</div>
                                {!result.free && (
                                    <div className="bc-result-stars">
                                        {[1, 2, 3].map((s) => <span key={s} style={{ opacity: s <= result.stars ? 1 : 0.2 }}>⭐</span>)}
                                    </div>
                                )}
                                {!result.free && result.stars < 3 && <div style={{ fontSize: 13, fontWeight: 800, color: '#6D4C41', marginBottom: 8 }}>Can you do it with fewer blocks? Try 🔁!</div>}
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button type="button" className="bc-btn light" onClick={() => setResult(null)}>↺ Play again</button>
                                    {!result.free && nextMissionIdx !== null && (
                                        <button type="button" className="bc-btn play" style={{ fontSize: 17 }} onClick={() => openMission(nextMissionIdx)}>Next mission ➜</button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{ fontSize: 50 }}>🤔</div>
                                <div style={{ fontSize: 24, fontWeight: 900 }}>Almost! Try again</div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#6D4C41', margin: '6px 0 12px' }}>💡 {mission.hint}</div>
                                <button type="button" className="bc-btn play" style={{ fontSize: 17 }} onClick={() => setResult(null)}>OK, let's fix it!</button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PuzzleBlockCoding;
